import React, { useEffect, useState } from 'react';
import { initGlobalTrackingListener } from '@/services/trackingService';
import { CONSENT_STORAGE_KEY, CONSENT_CHANGED_EVENT } from '@/components/shared/CookieConsent';
import { LazyImage } from '@/components/ui/LazyImage';

export function TrackingInjector() {
  // Read consent value. Default to NOT loading when value is absent or not 'granted'
  const [hasConsent, setHasConsent] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(CONSENT_STORAGE_KEY) === 'granted';
  });

  const [gaId, setGaId] = useState('');
  const [googleAdsId, setGoogleAdsId] = useState('');
  const [gtmId, setGtmId] = useState('');
  const [metaPixelId, setMetaPixelId] = useState('');
  const [linkedinInsightId, setLinkedinInsightId] = useState('');

  // Dynamically listen for consent changes so tags initialize without requiring a reload
  useEffect(() => {
    const handleConsentChange = () => {
      const isGranted = typeof window !== 'undefined' && localStorage.getItem(CONSENT_STORAGE_KEY) === 'granted';
      setHasConsent(isGranted);
    };

    window.addEventListener(CONSENT_CHANGED_EVENT, handleConsentChange);
    window.addEventListener('storage', handleConsentChange);

    return () => {
      window.removeEventListener(CONSENT_CHANGED_EVENT, handleConsentChange);
      window.removeEventListener('storage', handleConsentChange);
    };
  }, []);

  // Gated: only initialize automatic link/button click and form submission tracker if consent is granted
  useEffect(() => {
    if (hasConsent) {
      initGlobalTrackingListener();
    }
  }, [hasConsent]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch('/api/public/tracking-config');
        if (res.ok) {
          const data = await res.json();
          setGaId(data.gaId || '');
          setGoogleAdsId(data.googleAdsId || '');
          setGtmId(data.gtmId || '');
          setMetaPixelId(data.metaPixelId || '');
          setLinkedinInsightId(data.linkedinInsightId || '');
        }
      } catch (error) {
        console.warn('Error fetching tracking settings:', error);
      }
    };
    
    fetchSettings();
  }, []);

  useEffect(() => {
    // Inject NOTHING until consent is 'granted'. When absent or declined, do nothing.
    if (!hasConsent) {
      return;
    }

    // If we have a GA ID or Google Ads ID, inject gtag.js
    if (gaId || googleAdsId) {
      const primaryId = gaId || googleAdsId; // Use whichever exists as the main script src
      
      // Check if already injected
      if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${primaryId}`;
        document.head.appendChild(script);

        const initScript = document.createElement('script');
        initScript.innerHTML = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          gtag('js', new Date());
        `;
        document.head.appendChild(initScript);
      }

      // Configure individual tags if they exist
      if (gaId && !window.dataLayer?.some((e: any) => e[1] === gaId)) {
        window.dataLayer = window.dataLayer || [];
        function gtag(...args: any[]){window.dataLayer.push(args);}
        gtag('config', gaId);
      }
      
      if (googleAdsId && !window.dataLayer?.some((e: any) => e[1] === googleAdsId)) {
        window.dataLayer = window.dataLayer || [];
        function gtag(...args: any[]){window.dataLayer.push(args);}
        gtag('config', googleAdsId);
      }
    }

    // GTM Injection
    if (gtmId) {
      if (!document.querySelector('script[src*="googletagmanager.com/gtm.js"]')) {
        const script = document.createElement('script');
        script.innerHTML = `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${gtmId}');
        `;
        document.head.appendChild(script);
        
        const noscript = document.createElement('noscript');
        noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
        document.body.insertBefore(noscript, document.body.firstChild);
      }
    }

    // Meta Pixel Injection
    if (metaPixelId) {
      if (!document.getElementById('meta-pixel')) {
        const script = document.createElement('script');
        script.id = 'meta-pixel';
        script.innerHTML = `
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${metaPixelId}');
          fbq('track', 'PageView');
        `;
        document.head.appendChild(script);

        const noscript = document.createElement('noscript');
        noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${metaPixelId}&ev=PageView&noscript=1" />`;
        document.body.appendChild(noscript);
      }
    }

    // LinkedIn Insight Tag Injection
    if (linkedinInsightId) {
      if (!document.getElementById('linkedin-insight')) {
        // Set partner ID globally
        (window as any)._linkedin_data_partner_ids = (window as any)._linkedin_data_partner_ids || [];
        (window as any)._linkedin_data_partner_ids.push(linkedinInsightId);

        const script = document.createElement('script');
        script.id = 'linkedin-insight';
        script.innerHTML = `
          (function(l) {
            if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
            window.lintrk.q=[]}
            var s = document.getElementsByTagName("script")[0];
            var b = document.createElement("script");
            b.type = "text/javascript";b.async = true;
            b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
            s.parentNode.insertBefore(b, s);
          })(window.lintrk);
        `;
        document.head.appendChild(script);

        const noscript = document.createElement('noscript');
        noscript.innerHTML = `<img height="1" width="1" style="display:none;" alt="" src="https://px.ads.linkedin.com/collect/?pid=${linkedinInsightId}&fmt=gif" />`;
        document.body.appendChild(noscript);
      }
    }
  }, [gaId, googleAdsId, gtmId, metaPixelId, linkedinInsightId]);

  // This is a headless component, it doesn't render anything visible
  return null;
}
